import React, { ReactNode, useEffect, useState } from "react";
import { BundledProductRowFormOptions } from "../BundledProductRowFormOptions";
import { ColumnType } from "antd/lib/table";
import { BundledProductRowFormNameLabel } from "../BundledProductRowFormNameLabel/BundledProductRowFormNameLabel";
import { getCurrencySettings } from "@/Utils/Currency";
import { BundledQuoteProduct } from "@/Contracts/ProposalBuilder/Block";

import "./BundleProductDrawerTable.less";
import { EditOutlined, EnterOutlined } from "@ant-design/icons";
import { Popover, Radio, Button } from "antd";
import { ProductBillingType } from "@/Contracts/Products";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import {
	BundleProductDrawerTableProps,
	PricingNameInputProps
} from "./interface";
import { RadioChangeEvent } from "antd/es/radio/interface";
import { useAllowZeroOrNegativeQuantity } from "@/Hooks/Integrations/useAllowZeroOrNegativeQuantity";
import { ZMessage } from "@molecules";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { ZInputNumber, ZRadioGroup, ZTypography } from "@atoms";
import { ZTable } from "@organisms";
import { InputNumberWidths } from "@atoms/ZInputNumber";

export const PricingNameInput: React.FC<PricingNameInputProps> = ({
	selectedPricingId,
	pricingArray,
	onChange,
	disabled
}) => {
	const selectedPricing = pricingArray?.find(a => a.id === selectedPricingId);

	const handleChange = (event: RadioChangeEvent) => {
		onChange(event.target.value);
	};
	const content = (
		<ZRadioGroup
			onChange={handleChange}
			value={selectedPricingId}
			className="pricing-radio-group"
			disabled={!!disabled}
		>
			{pricingArray?.map(p => (
				<Radio key={p.id || undefined} value={p.id}>
					{p.name || "Default Pricing"}
				</Radio>
			))}
		</ZRadioGroup>
	);

	return selectedPricing ? (
		<>
			{!!pricingArray && pricingArray.length > 1 && (
				<Popover
					placement="rightTop"
					title={null}
					content={content}
					trigger="click"
				>
					<div className="selected-pricing-name">
						<EnterOutlined className="selected-pricing-name-icon" />
						{selectedPricing?.name || "Default Pricing"}
						<EditOutlined />
					</div>
				</Popover>
			)}

			{!!pricingArray && pricingArray.length === 1 && (
				<div className="selected-pricing-name">
					<EnterOutlined className="selected-pricing-name-icon" />
					{selectedPricing?.name || "Default Pricing"}
				</div>
			)}
		</>
	) : (
		<></>
	);
};

const BundleProductDrawerTable: React.FC<
	BundleProductDrawerTableProps
> = props => {
	const { negativeQuantity, zeroQuantity } = useAllowZeroOrNegativeQuantity();

	const editPermissions = [
		AllRolePermissions.QUOTE_BLOCK_ADD,
		AllRolePermissions.QUOTE_BLOCK_UPDATE,
		AllRolePermissions.QUOTE_BLOCK_BUNDLE_ADD,
		AllRolePermissions.QUOTE_BLOCK_BUNDLE_UPDATE
	];
	const addPermissions = [
		AllRolePermissions.QUOTE_BLOCK_ADD,
		AllRolePermissions.QUOTE_BLOCK_BUNDLE_ADD
	];

	// add existing product to the quote bundle
	const hasBundleProductAddPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		addPermissions
	);

	// create new product and add it to the quote bundle
	const hasProductCreateAndAddPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[
			AllRolePermissions.ITEM_READ,
			AllRolePermissions.ITEM_CREATE,
			...addPermissions
		]
	);

	const hasEditBundlePermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		editPermissions
	);

	const hasEditBundleCostPricePermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[
			...editPermissions,
			AllRolePermissions.QUOTE_BLOCK_EDIT_ALL,
			AllRolePermissions.QUOTE_BLOCK_BUNDLE_EDIT_ALL
		]
	);

	const [bundleProductsData, setBundleProductsData] = useState<
		BundledQuoteProduct[]
	>([]);

	useEffect(() => {
		const bundleProductDataSource = !!props.data.length
			? props.data.map((sku, index) => {
					return {
						...sku,
						index
					};
			  })
			: [];
		setBundleProductsData(bundleProductDataSource);
	}, [props.data]);

	const updateBundleProductOrder = props.updateBundleProductOrder;

	const handleDragEnd = ({ ...props }) => {
		const { oldIndex, newIndex } = props;
		const newBundledProducts = [...bundleProductsData];
		const destinationIndex = newIndex ? newIndex : 0;
		const sourceIndex = oldIndex ? oldIndex : 0;
		const [removed] = newBundledProducts.splice(sourceIndex, 1);
		newBundledProducts.splice(destinationIndex, 0, removed);

		setBundleProductsData(newBundledProducts);
		updateBundleProductOrder(newBundledProducts);
	};

	const toggleBundleProductOptional =
		(vendorProductId: string, optionalState: boolean) => () => {
			props.onRowChange(vendorProductId, { is_optional: !optionalState });
		};

	const handleBundledProductSellPriceChange =
		(vendorProductId: string) => (sell_price?: number | string | null) => {
			if (typeof sell_price == "undefined" || sell_price === null) {
				return;
			}

			props.onRowChange(vendorProductId, {
				sell_price: Number(sell_price)
			});
		};

	const handleBundledProductCostPriceChange =
		(vendorProductId: string) =>
		(selected_cost_price?: number | string | null) => {
			if (
				typeof selected_cost_price == "undefined" ||
				selected_cost_price === null
			) {
				return;
			}
			props.onRowChange(vendorProductId, {
				selected_cost_price: Number(selected_cost_price)
			});
		};

	const handleBundledProductQuantityChange =
		(vendorProductId: string) => (quantity?: number | string | null) => {
			if (!quantity && quantity !== 0) {
				return;
			}

			if (Number(quantity) === 0 && !zeroQuantity.value) {
				ZMessage.error(
					`Zero quantity is not supported by ${capitalizeFirstLetter(
						zeroQuantity?.name
					)}`
				);
				return;
			} else if (Number(quantity) < 0 && !negativeQuantity.value) {
				ZMessage.error(
					`Negative quantity is not supported by ${capitalizeFirstLetter(
						negativeQuantity.name
					)}`
				);
				return;
			}

			props.onRowChange(vendorProductId, { quantity: Number(quantity) });
		};

	const handleBundledProductSetupPriceChange =
		(vendorProductId: string) =>
		(selected_setup_price?: number | string | null) => {
			if (
				typeof selected_setup_price === "undefined" ||
				selected_setup_price === null
			) {
				return;
			}

			props.onRowChange(vendorProductId, {
				selected_setup_price: Number(selected_setup_price)
			});
		};

	const handlePricingChange =
		(selectedVendorProductId: string) => (newPricingId: string) => {
			const selectedProduct = props.data.find(
				a => a.vendor_product_id === selectedVendorProductId
			);
			if (!selectedProduct?.pricing?.length) {
				throw new Error(
					`There are no pricing in this product to change`
				);
			}
			const selectedPricingData = selectedProduct?.pricing?.find(
				a => a.id === newPricingId
			);

			if (!selectedPricingData) {
				throw new Error(
					`Invalid Pricing Id: expected one of ${selectedProduct.pricing
						?.map(a => a.id)
						.join(", ")}, received ${newPricingId}`
				);
			}

			props.onRowChange(selectedVendorProductId, {
				selected_pricing_id: newPricingId,
				selected_cost_price: selectedPricingData?.cost_price,
				selected_setup_price: selectedPricingData?.setup_price,
				selected_billing_period:
					selectedPricingData?.billing_period ||
					ProductBillingType.OneTime,
				sell_price: selectedPricingData?.sell_price
			});
		};

	const handleBundleProductChildDelete = (vendorProductId: string) => () => {
		setBundleProductsData(prevState =>
			prevState.filter(
				product => product.vendor_product_id !== vendorProductId
			)
		);
		props.onRowDelete(vendorProductId);
	};

	const handleEditDrawerOpen = (data: BundledQuoteProduct) => () =>
		props.onOpenEditDrawer(data);

	const bundleProductColumns: ColumnType<BundledQuoteProduct>[] = [
		{
			title: "Product name",
			dataIndex: "name",
			key: "name",
			width: 250,
			className: "drag-visible-z-table-row",
			render: (
				_name: string,
				product: BundledQuoteProduct
			): ReactNode => {
				return (
					<BundledProductRowFormNameLabel product={product}>
						<PricingNameInput
							pricingArray={product.pricing}
							selectedPricingId={product.selected_pricing_id}
							onChange={handlePricingChange(
								product.vendor_product_id
							)}
							disabled={
								!hasEditBundlePermission || props.isReadOnly
							}
						/>
						<label className="setup-price-wrap">
							<ZTypography.ZText className="setup-label-text">
								Setup Fee:
							</ZTypography.ZText>
							<ZInputNumber
								addOnAfter={getCurrencySettings().currency}
								disabled={
									!hasEditBundlePermission || props.isReadOnly
								}
								defaultValue={product.selected_setup_price}
								min={0}
								max={99999999999}
								onChange={handleBundledProductSetupPriceChange(
									product.vendor_product_id
								)}
								size="small"
								precision={2}
							/>
						</label>
					</BundledProductRowFormNameLabel>
				);
			}
		},
		{
			title: "Cost Price",
			dataIndex: "selected_cost_price",
			key: "selected_cost_price",
			className: "drag-visible-z-table-row",
			width: 150,
			render: (
				selected_cost_price: number,
				product: BundledQuoteProduct
			) => (
				<div className="table-input-wrap">
					<ZInputNumber
						addOnAfter={getCurrencySettings().currency}
						disabled={
							!hasEditBundleCostPricePermission ||
							props.isReadOnly
						}
						defaultValue={selected_cost_price}
						min={0}
						max={99999999999}
						onChange={handleBundledProductCostPriceChange(
							product.vendor_product_id
						)}
						width={InputNumberWidths.full}
						precision={2}
					/>
				</div>
			)
		},
		{
			title: "Selling Price",
			dataIndex: "sell_price",
			key: "sell_price",
			className: "drag-visible-z-table-row",
			width: 150,
			render: (sell_price: number, product: BundledQuoteProduct) => (
				<div className="table-input-wrap">
					<ZInputNumber
						addOnAfter={getCurrencySettings().currency}
						disabled={!hasEditBundlePermission || props.isReadOnly}
						defaultValue={sell_price}
						min={0}
						max={99999999999}
						onChange={handleBundledProductSellPriceChange(
							product.vendor_product_id
						)}
						width={InputNumberWidths.full}
						precision={2}
					/>
				</div>
			)
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			className: "drag-visible-z-table-row",
			width: 80,
			render: (quantity: number, product: BundledQuoteProduct) => (
				<div className="table-input-wrap">
					<ZInputNumber
						disabled={!hasEditBundlePermission || props.isReadOnly}
						defaultValue={quantity}
						min={Number.MIN_SAFE_INTEGER}
						max={99999999999}
						onChange={handleBundledProductQuantityChange(
							product.vendor_product_id
						)}
						width={InputNumberWidths.full}
					/>
				</div>
			)
		},
		...(hasBundleProductAddPermission
			? [
					{
						title: "",
						dataIndex: "name",
						key: "options",
						className: "drag-visible-z-table-row",
						width: 30,
						render: (
							_name: string,
							product: BundledQuoteProduct
						) => (
							<div className="table-more-option-wrap cursor-pointer">
								<BundledProductRowFormOptions
									deleteBundleProductChild={handleBundleProductChildDelete(
										product.vendor_product_id
									)}
									onEditDrawerOpen={handleEditDrawerOpen(
										product
									)}
									isOptional={product.is_optional}
									toggleBundleProductOptional={toggleBundleProductOptional(
										product.vendor_product_id,
										product.is_optional
									)}
									enableOnlyDelete={!hasEditBundlePermission}
									isReadonly={props.isReadOnly}
								/>
							</div>
						)
					}
			  ]
			: [])
	];
	return (
		<ZTable
			className="bundled-product-table"
			columns={bundleProductColumns}
			dataSource={bundleProductsData}
			size="small"
			pagination={false}
			loading={false}
			rowKey="vendor_product_id"
			footer={() =>
				!props.isReadOnly &&
				(hasBundleProductAddPermission ||
					hasProductCreateAndAddPermission) && (
					<div className="bundle-product-table-footer">
						{hasBundleProductAddPermission && (
							<Button
								block
								htmlType="button"
								onClick={props.onAddProductButtonClick}
								className="mr-2"
							>
								Add Product
							</Button>
						)}
						{hasProductCreateAndAddPermission && (
							<Button
								block
								htmlType="button"
								onClick={props.onCreateProductButtonClick}
							>
								Create Product
							</Button>
						)}
					</div>
				)
			}
			isDraggable={false}
			onDragEnd={handleDragEnd}
			draggableRowInDrawers={true}
		/>
	);
};

export default BundleProductDrawerTable;
